import { Controller } from '@hotwired/stimulus';
import {
  setDisplayBlockByIds,
  submitSelectedStudentChoiceCourses,
  showFinishCardContentAndSection,
} from '../utils/case_experience_in_rails';
import { trapFocus, releaseFocusTrap } from '../utils/FocusTrap';
import { seemsOffline } from '../utils/connectivity';

// Connects to data-controller="finish-case-card"

export default class extends Controller {
  static values = {
    documentId: Number,
    userId: Number,
    url: String,
    caseName: String,
    timeOnCase: String,
  };

  static targets = [
    'lowTimeModal',
    'finishCardContent',
    'confirmButton',
    'studentChoiceSubmit',
    'studentChoiceAcknowledge',
    'studentChoiceCheckbox',
  ];

  initialize() {
    this.enableConfirmHandler =
      this.enableConfirmButton.bind(this);
  }

  connect() {
    this.setupRadioListeners();
    this.setupIntersectionObserver();
    this.enableConfirmButton();
  }

  disconnect() {
    this.removeRadioListeners();

    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
  }

  setupRadioListeners() {
    const radios = this.element.querySelectorAll(
      'input[type="radio"][name="action"]',
    );

    radios.forEach((radio) => {
      radio.addEventListener(
        'change',
        this.enableConfirmHandler,
      );
    });
  }

  removeRadioListeners() {
    const radios = this.element.querySelectorAll(
      'input[type="radio"][name="action"]',
    );

    radios.forEach((radio) => {
      radio.removeEventListener(
        'change',
        this.enableConfirmHandler,
      );
    });
  }

  enableConfirmButton() {
    if (!this.hasConfirmButtonTarget) return;

    const checked = this.element.querySelector(
      'input[type="radio"][name="action"]:checked',
    );

    this.confirmButtonTarget.disabled = !checked;
  }

  selectRadioFromLabel(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    const forId = event.currentTarget.getAttribute('for');
    if (!forId) return;
    const input = document.getElementById(forId);
    if (!input || input.type !== 'radio' || input.name !== 'action') return;
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  async submitSelectedStudentChoiceCoursesForSection(event, lowTime = false) {
    // Collect selected student-choice course IDs
    const selectedCourseCheckboxes = this.studentChoiceCheckboxTargets || [];
    const selectedCourseIds = Array.from(selectedCourseCheckboxes).filter(cb => cb.checked).map(cb => cb.value);

    // If any are selected, call the API before proceeding
    if (selectedCourseIds.length > 0 && this.documentIdValue) {
      if (lowTime) {
        this.confirmButtonTarget.setAttribute('aria-busy', 'true');
        this.confirmButtonTarget.disabled = true;
      }
      try {
        await submitSelectedStudentChoiceCourses(this.documentIdValue, selectedCourseIds);
      } catch (error) {
        // Optionally show error to user
        window.alert(
          seemsOffline()
            ? "This case can't be added as a student-choice case while offline. Try again when you have an internet connection."
            : 'Failed to add as student-choice case. Please try again.',
        );
        this.confirmButtonTarget.removeAttribute('aria-busy');
        this.confirmButtonTarget.disabled = false;
        event.preventDefault();
        return;
      }
      if (lowTime) {
        this.confirmButtonTarget.removeAttribute('aria-busy');
        this.confirmButtonTarget.disabled = false;
      }
    }
  }

  async handleConfirm(event) {
    await this.submitSelectedStudentChoiceCoursesForSection(event, true);

    const acknowledgeRadio = this.element.querySelector(
      'input[name="action"][value="acknowledgeLowTime"]',
    );
    const resetCaseRadio = this.element.querySelector(
      'input[name="action"][value="resetCase"]',
    );

    if (acknowledgeRadio?.checked) {
      this.showFinishCard();
      event.preventDefault();
      return;
    }

    if (resetCaseRadio?.checked) {
      const resetBtn = this.element.querySelector(
        '[data-reset-case-target="resetCaseButton"]',
      );
      resetBtn?.click();
      event.preventDefault();
    }
  }

  showFinishCard() {
    if (this.hasLowTimeModalTarget) {
      releaseFocusTrap(this.lowTimeModalTarget);
      this.lowTimeModalTarget.style.display = 'none';
    }

    if (this.hasFinishCardContentTarget) {
      this.finishCardContentTarget.style.display = '';
    }

    setDisplayBlockByIds(['finish-card-section']);
  }

  setupIntersectionObserver() {
    this._observer = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.onVisible();
          }
        });
      },
      { threshold: 0.1 },
    );

    this._observer.observe(this.element);
  }

  async onVisible() {
    const bar = document.querySelector('.progress-container[role="progressbar"]');
    const progress = parseInt(bar?.getAttribute('aria-valuenow') || '', 10);
    if (progress === 100) setDisplayBlockByIds(['end_of_case_text']);

    // Fire only once
    this._observer?.unobserve(this.element);
  }

  // --- Quiz AJAX Button logic ---
  async finish(event) {
    event.preventDefault();
    const button = event.currentTarget;
    button.setAttribute("aria-busy", "true");
    button.setAttribute("disabled", "disabled");
    // Add a visual loading indicator (optional, can be styled in CSS)
    let loader = document.createElement('span');
    loader.className = 'finish-card-loader';
    loader.setAttribute('aria-hidden', 'true');
    loader.innerHTML = 'Loading...';
    button.appendChild(loader);

    let hadError = false;
    try {
      const url = button.dataset.finishCaseCardUrlValue || this.urlValue;
      const caseName = button.dataset.finishCaseCardCaseNameValue || this.caseNameValue;
      const timeOnCase = button.dataset.finishCaseCardTimeOnCaseValue || this.timeOnCaseValue;
      const response = await fetch(url);
      if (!response.ok) {
        hadError = true;
        console.log(`Failed to load quiz: ${response.status} ${response.statusText}`);
        return;
      }
      const html = await response.text();
      if (window.Turbo && Turbo.renderStreamMessage) {
        await Turbo.renderStreamMessage(html);
      }
      const el = document.getElementById("openQuestionContainer");
      if (el) {
        el.classList.remove("hidden");
        releaseFocusTrap(el);
        trapFocus(el);
        const obs = new MutationObserver(() => {
          if (el.classList.contains('hidden')) {
            releaseFocusTrap(el);
            obs.disconnect();
          }
        });
        obs.observe(el, { attributes: true, attributeFilter: ['class'] });
      }
      if (window.analytics && typeof window.analytics.track === "function") {
        window.analytics.track("open_questions", {
          page: window.location.href,
          source: "Case Page",
          case_name: caseName,
          time_on_case: timeOnCase
        });
      }
    } catch (e) {
      hadError = true;
      throw e;
    } finally {
      const loaderEl = button.querySelector('.finish-card-loader');
      if (loaderEl) loaderEl.remove();
      button.removeAttribute("aria-busy");
      if (hadError) {
        button.removeAttribute("disabled");
        button.focus(); // Restore focus for accessibility only on error
      }
      // On success, button stays disabled and focus is not overridden
    }
  }

  updateStudentChoiceButton = () => {
    // Find all checkboxes with the target
    if (!this.hasStudentChoiceSubmitTarget) return;
    const checkboxes = this.studentChoiceCheckboxTargets || [];
    const anyChecked = checkboxes.some(cb => cb.checked);
    this.studentChoiceSubmitTarget.disabled = !anyChecked;
  }

  async studentChoiceAcknowledge(event) {
    event.preventDefault();
    showFinishCardContentAndSection();
  }

  async studentChoiceSubmit(event) {
    await this.submitSelectedStudentChoiceCoursesForSection(event, false);
    showFinishCardContentAndSection();
  }
}
